import { Container, Group, Image, LoadingOverlay, RingProgress, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormWithConfig } from './components/formConfiguration';
import { useEffect, useMemo, useState } from 'react';
import { PersonalInformation } from './components/PersonalInformation';
import { AuthInformation } from './components/AuthInformation';
import { RegistrationResult } from './components/RegistrationResult';
import { ChatTariffs } from './components/ChatTariffs';
import { VideoTariffs } from './components/VideoTariffs';
import { ApiError, endpoints } from './api/index'
import { LanguageSelector } from './components/LanguageSelector'
import { resolveLanguage } from './i18n'
import {
  RESULT_STEP,
  firstErrorStep,
  formatToBackendCompatibleJSON,
  renderableFields,
  toFormField,
} from './submission'

const App = () => {
  const { t, i18n } = useTranslation()
  const stepParam = new URLSearchParams(window.location.search).get('step');
  const [step, setStep] = useState(+(stepParam ?? 0) ?? 0);
  const [isFetching, setIsFetching] = useState(false);
  const [result, setResult] = useState<'success' | 'failure' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const form = useFormWithConfig();

  const fail = () => {
    setResult('failure');
    setStep(RESULT_STEP);
  };

  // The form re-validates on language change to retranslate its own messages,
  // which drops the server's. Only a new submission can retranslate those.
  useEffect(() => {
    if (Object.keys(serverErrors).length) {
      form.setErrors(errors => ({ ...errors, ...serverErrors }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const goToStep = (next: any) => {
    setServerErrors({});
    setSubmitError(null);
    setStep(next);
  };

  const onSubmit = async () => {
    const { hasErrors, errors } = form.validate();
    if (hasErrors) {
      goToStep(firstErrorStep(Object.keys(errors)));
      return;
    }
    setSubmitError(null);
    setServerErrors({});
    setIsFetching(true);
    const { payload, chatOrder } = formatToBackendCompatibleJSON(form.values, t);
    const renderable = renderableFields(form.values);
    try {
      const res = await endpoints.sendForm(payload, resolveLanguage(i18n));
      if (res.code === 0) {
        setResult('success');
        setStep(RESULT_STEP);
      } else {
        fail();
      }
    } catch (error) {
      if (!(error instanceof ApiError)) {
        fail();
        return;
      }
      const validationErrors = error.validationErrors;
      if (validationErrors?.length) {
        const fieldErrors: Record<string, string> = {};
        const unmapped: string[] = [];
        validationErrors.forEach(({ field, message }) => {
          const formField = toFormField(field, chatOrder);
          if (renderable.has(formField)) {
            fieldErrors[formField] = message;
          } else {
            unmapped.push(message);
          }
        });
        form.setErrors(fieldErrors);
        setServerErrors(fieldErrors);
        setSubmitError(unmapped.join(' ') || null);
        setStep(firstErrorStep(Object.keys(fieldErrors)));
      } else if (error.status === 429) {
        setSubmitError(t('common.validation.tooManyAttempts'));
      } else {
        fail();
      }
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [step]);

  const progressProps = useMemo(() => {
    const title = step === RESULT_STEP ? (result === 'success' ? t('common.finished') : t('common.error')) : t('common.step', { step: step + 1 });
    let subtitle = '';
    switch (step) {
      case 0:
        subtitle = t('account.title');
        break;
      case 1:
        subtitle = t('about.title');
        break;
      case 2:
        subtitle = t('videoconsultations.title');
        break;
      case 3:
        subtitle = t('chat.title');
        break;
      case 4:
        subtitle = result === 'success' ? t('success.finish') : t('success.error');
        break;
      default:
        break;
    }

    return {
      title,
      subtitle,
    };
  }, [step, result, t]);
  return (
    <>
      <Image
        fit={'cover'}
        width={'100%'}
        height={192}
        src={'./assets/header.png'}
        alt={'Express.doctor header'}
      />
      <Container style={{ position: 'relative' }}>
        <Group mt="xl" spacing={8} position={'left'}>
          <RingProgress
            size={68}
            thickness={6}
            roundCaps
            sections={[{ value: (step / 4) * 100, color: 'blue' }]}
            label={
              <Text color="blue" weight={700} align="center" size="xs">
                {`${step + 1}/5`}
              </Text>
            }
          />
          <Stack spacing={0}>
            <Text weight={'bold'}>{progressProps.title}</Text>
            <Text>{progressProps.subtitle}</Text>
          </Stack>

          <div style={{marginLeft: 'auto'}}>
            <LanguageSelector />
          </div>
        </Group>
        <Container my="xl" px={0}>
          {/* Rendered outside the steps: a 422 can carry a message we cannot
              attach to an input while sending the user to another step. */}
          {submitError && (
            <Text color="red" size="sm" mb="md">
              {submitError}
            </Text>
          )}
          {step === 0 && <AuthInformation setStep={goToStep} form={form} />}
          {step === 1 && <PersonalInformation setStep={goToStep} form={form} />}
          {step === 2 && <VideoTariffs setStep={goToStep} form={form} />}
          {step === 3 && <ChatTariffs onSubmit={onSubmit} form={form} />}
          {step === RESULT_STEP && result && <RegistrationResult variant={result} />}
        </Container>
        <LoadingOverlay visible={isFetching} loaderProps={{ color: 'blue' }} />
      </Container>
    </>
  );
};

export default App;
